(() => {
  const SESSION_KEY = 'tgd_agent_session_v1';
  const HISTORY_KEY = 'tgd_agent_history_v3';
  const WELCOME_KEY = 'tgd_agent_welcome_v1';
  const QUOTE_KEY = 'tgd_agent_quote_v1';
  const MAX_HISTORY = 40;
  const REQUEST_TIMEOUT = 75000;

  const storage = {
    get(key) {
      try {
        return window.sessionStorage.getItem(key);
      } catch {
        return null;
      }
    },

    set(key, value) {
      try {
        window.sessionStorage.setItem(key, value);
      } catch {}
    },

    remove(key) {
      try {
        window.sessionStorage.removeItem(key);
      } catch {}
    }
  };

  function initAgent(root) {
    if (!root || root.dataset.tgdAgentReady === 'true') return;

    root.dataset.tgdAgentReady = 'true';

    const launcher = root.querySelector('[data-tgd-agent-open]');
    const panel = root.querySelector('[data-tgd-agent-panel]');
    const closeButton = root.querySelector('[data-tgd-agent-close]');
    const messages = root.querySelector('[data-tgd-agent-messages]');
    const form = root.querySelector('[data-tgd-agent-form]');
    const input = root.querySelector('[data-tgd-agent-input]');
    const sendButton = root.querySelector('[data-tgd-agent-send]');

    if (
      !launcher ||
      !panel ||
      !closeButton ||
      !messages ||
      !form ||
      !input ||
      !sendButton
    ) {
      return;
    }

    const endpoint = root.dataset.endpoint || '';
    const apiKey = root.dataset.apiKey || '';
    const language = (root.dataset.language || 'en').toLowerCase();
    const isSpanish = language.startsWith('es');

    let pending = false;
    let transitionInProgress = false;

    const copy = isSpanish
      ? {
          greeting:
            'Hola, soy Goodie. Puedo ayudarte a encontrar productos, comparar opciones, resolver dudas de personalización y preparar tu proyecto para solicitar presupuesto. ¿Qué quieres crear?',
          placeholder: 'Pregunta a Goodie…',
          thinking: 'Pensando…',
          genericError:
            'No he podido completar la respuesta. Inténtalo de nuevo.',
          timeout:
            'La respuesta está tardando más de lo esperado. Inténtalo de nuevo.',
          rateLimit: (seconds) =>
            `Has enviado demasiados mensajes. Inténtalo de nuevo en ${seconds} s.`,
          newSession:
            'La sesión anterior había caducado. He iniciado una nueva.',
          missingConfig:
            'Goodie todavía no está configurado correctamente.'
        }
      : {
          greeting:
            'Hi, I’m Goodie. I can help you find products, compare options, answer personalisation questions and prepare your project for a quote. What would you like to create?',
          placeholder: 'Ask Goodie…',
          thinking: 'Thinking…',
          genericError:
            'I could not complete the response. Please try again.',
          timeout:
            'The response is taking longer than expected. Please try again.',
          rateLimit: (seconds) =>
            `Too many messages. Please try again in ${seconds}s.`,
          newSession:
            'The previous session had expired. I started a new one.',
          missingConfig:
            'Goodie is not configured correctly yet.'
        };

    input.placeholder = copy.placeholder;

    function pageContext() {
      const context = {
        page_type: root.dataset.pageType || null,
        path: root.dataset.pagePath || window.location.pathname,
        title: root.dataset.pageTitle || document.title,
        url: window.location.href
      };

      if (
        root.dataset.productId ||
        root.dataset.productHandle
      ) {
        context.product = {
          id: root.dataset.productId || null,
          handle: root.dataset.productHandle || null,
          title: root.dataset.productTitle || null,
          type: root.dataset.productType || null,
          vendor: root.dataset.productVendor || null
        };
      }

      return context;
    }

    function loadHistory() {
      const raw = storage.get(HISTORY_KEY);

      if (!raw) return [];

      try {
        const parsed = JSON.parse(raw);

        return Array.isArray(parsed)
          ? parsed.slice(-MAX_HISTORY)
          : [];
      } catch {
        return [];
      }
    }

    function saveHistory(history) {
      storage.set(
        HISTORY_KEY,
        JSON.stringify(
          history.slice(-MAX_HISTORY)
        )
      );
    }

    let history = loadHistory();

    function renderInline(parent, text) {
      const parts = String(text).split(
        /(\*\*[^*]+\*\*)/g
      );

      parts.forEach((part) => {
        if (
          part.startsWith('**') &&
          part.endsWith('**') &&
          part.length > 4
        ) {
          const strong =
            document.createElement('strong');

          strong.textContent =
            part.slice(2, -2);

          parent.appendChild(strong);
        } else {
          parent.appendChild(
            document.createTextNode(part)
          );
        }
      });
    }

    function createMessage(
      role,
      text,
      options = {}
    ) {
      const item =
        document.createElement('div');

      item.className =
        `tgd-agent__message tgd-agent__message--${role}`;

      if (options.status) {
        item.classList.add(
          'tgd-agent__message--status'
        );

        item.textContent = text;
      } else {
        const blocks = String(text)
          .trim()
          .split(/\n{2,}/);

        blocks.forEach((block) => {
          const paragraph =
            document.createElement('p');

          const lines =
            block.split('\n');

          lines.forEach(
            (line, index) => {
              if (index > 0) {
                paragraph.appendChild(
                  document.createElement('br')
                );
              }

              renderInline(
                paragraph,
                line
              );
            }
          );

          item.appendChild(paragraph);
        });
      }

      messages.appendChild(item);

      messages.scrollTop =
        messages.scrollHeight;

      return item;
    }

    function productUrl(handle) {
      const rootPath =
        window.Shopify?.routes?.root || '/';

      const normalizedRoot =
        rootPath.endsWith('/')
          ? rootPath
          : `${rootPath}/`;

      return `${normalizedRoot}products/${encodeURIComponent(handle)}`;
    }

    function createProductCards(products) {
      if (
        !Array.isArray(products) ||
        products.length === 0
      ) {
        return null;
      }

      const rail =
        document.createElement('div');

      rail.className =
        'tgd-agent__products';

      rail.setAttribute(
        'role',
        'list'
      );

      products
        .slice(0, 4)
        .forEach((product) => {
          if (
            !product ||
            !product.shopify_handle
          ) {
            return;
          }

          const name = isSpanish
            ? product.name_es ||
              product.name_en ||
              product.ref ||
              'Producto'
            : product.name_en ||
              product.name_es ||
              product.ref ||
              'Product';

          const card =
            document.createElement('a');

          card.className =
            'tgd-agent__product-card';

          card.href =
            productUrl(
              product.shopify_handle
            );

          card.setAttribute(
            'role',
            'listitem'
          );

          card.setAttribute(
            'aria-label',
            name
          );

          if (product.image_url) {
            const imageWrap =
              document.createElement('div');

            imageWrap.className =
              'tgd-agent__product-image-wrap';

            const image =
              document.createElement('img');

            image.className =
              'tgd-agent__product-image';

            image.src =
              product.image_url;

            image.alt = name;
            image.loading = 'lazy';
            image.decoding = 'async';

            imageWrap.appendChild(image);
            card.appendChild(imageWrap);
          }

          const body =
            document.createElement('div');

          body.className =
            'tgd-agent__product-body';

          const meta =
            document.createElement('div');

          meta.className =
            'tgd-agent__product-meta';

          meta.textContent = [
            product.brand,
            product.ref
          ]
            .filter(Boolean)
            .join(' · ');

          const title =
            document.createElement('div');

          title.className =
            'tgd-agent__product-title';

          title.textContent = name;

          body.appendChild(meta);
          body.appendChild(title);

          const detailParts = [];

          if (product.gsm) {
            detailParts.push(
              `${product.gsm} g/m²`
            );
          }

          if (product.composition) {
            detailParts.push(
              product.composition
            );
          }

          if (detailParts.length > 0) {
            const detail =
              document.createElement('div');

            detail.className =
              'tgd-agent__product-detail';

            detail.textContent =
              detailParts.join(' · ');

            body.appendChild(detail);
          }

          card.appendChild(body);
          rail.appendChild(card);
        });

      if (!rail.children.length) {
        return null;
      }

      messages.appendChild(rail);

      messages.scrollTop =
        messages.scrollHeight;

      return rail;
    }

    function addPersistentMessage(
      role,
      text,
      extra = {}
    ) {
      createMessage(role, text);

      if (
        Array.isArray(extra.products) &&
        extra.products.length > 0
      ) {
        createProductCards(
          extra.products
        );
      }

      history.push({
        role,
        text,
        products:
          Array.isArray(extra.products)
            ? extra.products.slice(0, 4)
            : []
      });

      saveHistory(history);
    }

    function restoreMessages() {
      messages.innerHTML = '';

      history.forEach((message) => {
        if (
          !message ||
          typeof message.text !== 'string'
        ) {
          return;
        }

        createMessage(
          message.role === 'user'
            ? 'user'
            : 'assistant',
          message.text
        );

        if (
          Array.isArray(message.products) &&
          message.products.length > 0
        ) {
          createProductCards(
            message.products
          );
        }
      });
    }

    function ensureWelcome() {
      if (
        history.length > 0 ||
        storage.get(WELCOME_KEY) === 'true'
      ) {
        return;
      }

      storage.set(
        WELCOME_KEY,
        'true'
      );

      addPersistentMessage(
        'assistant',
        copy.greeting
      );
    }
function openPanel() {
  panel.hidden = false;

  panel.setAttribute(
    'aria-hidden',
    'false'
  );

  launcher.setAttribute(
    'aria-expanded',
    'true'
  );

  window.requestAnimationFrame(() => {
    root.classList.add(
      'tgd-agent--open'
    );

    window.requestAnimationFrame(() => {
      input.focus({
        preventScroll: true
      });
    });
  });

  ensureWelcome();

  messages.scrollTop =
    messages.scrollHeight;
}

function closePanel() {
  root.classList.remove(
    'tgd-agent--open'
  );

  panel.setAttribute(
    'aria-hidden',
    'true'
  );

  launcher.setAttribute(
    'aria-expanded',
    'false'
  );

  window.setTimeout(() => {
    if (
      !root.classList.contains(
        'tgd-agent--open'
      )
    ) {
      panel.hidden = true;
    }
  }, 470);

  launcher.focus({
    preventScroll: true
  });
}

function resizeInput() {
      const maxHeight = 128;

      input.style.height = '44px';
      input.style.overflowY = 'hidden';

      const nextHeight =
        Math.min(
          Math.max(
            input.scrollHeight,
            44
          ),
          maxHeight
        );

      input.style.height =
        `${nextHeight}px`;

      if (
        input.scrollHeight >
        maxHeight
      ) {
        input.style.overflowY = 'auto';
      }
    }

    function setPending(value) {
      pending = value;

      input.disabled = value;
      sendButton.disabled = value;

      form.setAttribute(
        'aria-busy',
        value ? 'true' : 'false'
      );
    }

    function extractProductHandles(text) {
      const handles = [];
      const pattern =
        /\/products\/([a-z0-9-]+)/gi;

      let match;

      while (
        (match = pattern.exec(text)) !==
        null
      ) {
        const handle = match[1];

        if (
          handle &&
          !handles.includes(handle)
        ) {
          handles.push(handle);
        }
      }

      return handles.slice(0, 4);
    }

    async function fetchShopifyProduct(
      handle
    ) {
      const rootPath =
        window.Shopify?.routes?.root ||
        '/';

      const normalizedRoot =
        rootPath.endsWith('/')
          ? rootPath
          : `${rootPath}/`;

      try {
        const response =
          await fetch(
            `${normalizedRoot}products/${encodeURIComponent(
              handle
            )}.js`,
            {
              credentials: 'same-origin'
            }
          );

        if (!response.ok) {
          return null;
        }

        const product =
          await response.json();

        return {
          ref: null,

          name_es:
            product.title ||
            handle,

          name_en:
            product.title ||
            handle,

          brand:
            product.vendor ||
            null,

          image_url:
            product.featured_image ||
            product.images?.[0] ||
            null,

          shopify_handle:
            product.handle ||
            handle,

          gsm: null,

          composition: null,

          materials: [],

          matching_colors: []
        };
      } catch {
        return null;
      }
    }
    function normalizeBackendProducts(products) {
      if (!Array.isArray(products)) {
        return [];
      }

      return products
        .filter(
          (product) =>
            product &&
            typeof product.shopify_handle === 'string' &&
            product.shopify_handle
        )
        .map((product) => ({
          ref: product.ref || null,

          name_es:
            product.name_es || null,

          name_en:
            product.name_en || null,

          brand:
            product.brand || null,

          image_url:
            product.image_url || null,

          shopify_handle:
            product.shopify_handle,

          gsm:
            product.gsm || null,

          composition:
            product.composition || null,

          materials:
            Array.isArray(product.materials)
              ? product.materials
              : [],

          matching_colors:
            Array.isArray(product.matching_colors)
              ? product.matching_colors
              : []
        }));
    }

    function fallbackProduct(handle) {
      return {
        ref:
          handle.toUpperCase(),

        name_es:
          'Ver producto',

        name_en:
          'View product',

        brand:
          null,

        image_url:
          null,

        shopify_handle:
          handle,

        gsm:
          null,

        composition:
          null,

        materials:
          [],

        matching_colors:
          []
      };
    }

    async function resolveProducts(
      payloadProducts,
      answer
    ) {
      const backendProducts =
        normalizeBackendProducts(
          payloadProducts
        );

      const handles =
        extractProductHandles(
          answer
        );

      const byHandle =
        new Map();

      backendProducts.forEach(
        (product) => {
          byHandle.set(
            product.shopify_handle,
            product
          );
        }
      );

      for (const handle of handles) {
        if (
          byHandle.has(handle)
        ) {
          continue;
        }

        const shopifyProduct =
          await fetchShopifyProduct(
            handle
          );

        byHandle.set(
          handle,
          shopifyProduct ||
            fallbackProduct(handle)
        );
      }

      return Array.from(
        byHandle.values()
      ).slice(0, 4);
    }
    async function requestAgent(
      message,
      allowSessionRetry = true
    ) {
      const sessionId =
        storage.get(SESSION_KEY);

      const controller =
        new AbortController();

      const timeoutId =
        window.setTimeout(
          () => controller.abort(),
          REQUEST_TIMEOUT
        );

      const body = {
        message,
        language,
        page_context: pageContext()
      };

      if (sessionId) {
        body.session_id =
          sessionId;
      }

      try {
        const response =
          await fetch(endpoint, {
            method: 'POST',

            headers: {
              Authorization:
                `Bearer ${apiKey}`,

              apikey: apiKey,

              'Content-Type':
                'application/json'
            },

            body:
              JSON.stringify(body),

            signal:
              controller.signal
          });

        let payload = {};

        try {
          payload =
            await response.json();
        } catch {}

        if (
          response.status === 404 &&
          sessionId &&
          allowSessionRetry
        ) {
          storage.remove(
            SESSION_KEY
          );

          addPersistentMessage(
            'assistant',
            copy.newSession
          );

          return requestAgent(
            message,
            false
          );
        }

        if (
          response.status === 429
        ) {
          const retryAfter =
            Number(
              payload.retry_after_seconds ||
              response.headers.get(
                'Retry-After'
              ) ||
              60
            );

          throw new Error(
            `RATE_LIMIT:${Math.max(
              1,
              Math.ceil(retryAfter)
            )}`
          );
        }

        if (!response.ok) {
          throw new Error(
            payload.error ||
            `HTTP_${response.status}`
          );
        }

        if (
          typeof payload.session_id ===
            'string' &&
          payload.session_id
        ) {
          storage.set(
            SESSION_KEY,
            payload.session_id
          );
        }

        if (
          Array.isArray(
            payload.actions
          )
        ) {
          const quoteAction =
            payload.actions.find(
              (action) =>
                action &&
                action.type ===
                  'quote_request_prepared'
            );

          if (quoteAction) {
            storage.set(
              QUOTE_KEY,

              JSON.stringify({
                status:
                  quoteAction.status ||
                  'prepared'
              })
            );

            root.dataset.quoteReady =
              'true';
          }
        }

        if (
          typeof payload.answer !==
            'string' ||
          !payload.answer.trim()
        ) {
          throw new Error(
            'EMPTY_ANSWER'
          );
        }

        const products =
          await resolveProducts(
            payload.ui?.products,
            payload.answer
          );

        return {
          answer:
            payload.answer.trim(),

          products
        };
      } finally {
        window.clearTimeout(
          timeoutId
        );
      }
    }

    async function submitMessage() {
      const message =
        input.value.trim();

      if (
        !message ||
        pending
      ) {
        return;
      }

      if (
        !endpoint ||
        !apiKey
      ) {
        addPersistentMessage(
          'assistant',
          copy.missingConfig
        );

        return;
      }

      addPersistentMessage(
        'user',
        message
      );

      input.value = '';

      resizeInput();
      setPending(true);

      const statusMessage =
        createMessage(
          'assistant',
          copy.thinking,
          {
            status: true
          }
        );

      try {
        const result =
          await requestAgent(
            message
          );

        statusMessage.remove();

        addPersistentMessage(
          'assistant',
          result.answer,
          {
            products:
              result.products
          }
        );
      } catch (error) {
        statusMessage.remove();

        const reason =
          error instanceof Error
            ? error.message
            : '';

        if (
          reason.startsWith(
            'RATE_LIMIT:'
          )
        ) {
          const seconds =
            Number(
              reason.split(':')[1] ||
              60
            );

          addPersistentMessage(
            'assistant',
            copy.rateLimit(
              seconds
            )
          );
        } else if (
          error &&
          error.name ===
            'AbortError'
        ) {
          addPersistentMessage(
            'assistant',
            copy.timeout
          );
        } else {
          addPersistentMessage(
            'assistant',
            copy.genericError
          );
        }
      } finally {
        setPending(false);

        input.focus({
          preventScroll: true
        });
      }
    }

    launcher.addEventListener(
      'click',
      openPanel
    );

    closeButton.addEventListener(
      'click',
      closePanel
    );

    form.addEventListener(
      'submit',
      (event) => {
        event.preventDefault();

        submitMessage();
      }
    );

    input.addEventListener(
      'input',
      resizeInput
    );

    input.addEventListener(
      'keydown',
      (event) => {
        if (
          event.key === 'Enter' &&
          !event.shiftKey
        ) {
          event.preventDefault();

          submitMessage();
        }
      }
    );

    document.addEventListener(
      'keydown',
      (event) => {
        if (
          event.key === 'Escape' &&
          !panel.hidden
        ) {
          closePanel();
        }
      }
    );

    restoreMessages();
    resizeInput();
  }

  function boot() {
    document
      .querySelectorAll(
        '[data-tgd-agent-root]'
      )
      .forEach(initAgent);
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      boot,
      {
        once: true
      }
    );
  } else {
    boot();
  }
})();